import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { ICONS } from '@/constants/icons';
import { BulkImportJobStatus } from '@/types/bulkImport';
import { useTimezone } from '@/contexts/TimezoneContext';
import { cn } from '@/utils/cn';

/**
 * BulkImportProgress Component
 *
 * Displays import progress with stats and cancel option
 */

interface BulkImportProgressProps {
  status: BulkImportJobStatus;
  onCancel?: () => void;
  isCancelling?: boolean;
}

const STATUS_CONFIG = {
  PENDING: {
    labelKey: 'bulk_import_status_pending',
    color: 'bg-gray-100 text-gray-700',
    icon: ICONS.CLOCK,
  },
  PROCESSING: {
    labelKey: 'bulk_import_status_processing',
    color: 'bg-soft-mint text-dark-teal',
    icon: ICONS.CLOCK,
  },
  COMPLETED: {
    labelKey: 'bulk_import_status_completed',
    color: 'bg-green-100 text-green-700',
    icon: ICONS.CHECK,
  },
  FAILED: {
    labelKey: 'bulk_import_status_failed',
    color: 'bg-red-100 text-red-700',
    icon: ICONS.CAUTION,
  },
  CANCELLED: {
    labelKey: 'bulk_import_status_cancelled',
    color: 'bg-gray-100 text-gray-700',
    icon: ICONS.CLOSE,
  },
} as const;

export const BulkImportProgress = ({
  status,
  onCancel,
  isCancelling = false,
}: BulkImportProgressProps) => {
  const { t } = useTranslation('common');
  const { formatDateTime } = useTimezone();
  const statusConfig = STATUS_CONFIG[status.status];
  const canCancel = status.status === 'PENDING' || status.status === 'PROCESSING';

  return (
    <div className="space-y-6">
      {/* Status Badge and Import Type */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className={cn('inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium', statusConfig.color)}>
            <Icon name={statusConfig.icon} size="sm" ariaHidden />
            <span>{t(statusConfig.labelKey)}</span>
          </div>
          {status.importType && (
            <div className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
              {status.importType === 'VARIATIONS' ? t('bulk_import_type_variations') : t('bulk_import_type_posts')}
            </div>
          )}
        </div>

        {canCancel && onCancel && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onCancel}
            disabled={isCancelling}
          >
            {isCancelling ? t('bulk_import_cancelling') : t('cancel')}
          </Button>
        )}
      </div>

      {/* Progress Bar */}
      <div>
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>{t('bulk_import_progress')}</span>
          <span className="font-medium">{status.percentComplete}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-300',
              status.status === 'COMPLETED' && 'bg-green-500',
              status.status === 'FAILED' && 'bg-red-500',
              status.status === 'CANCELLED' && 'bg-gray-500',
              (status.status === 'PENDING' || status.status === 'PROCESSING') && 'bg-primary-700'
            )}
            style={{ width: `${status.percentComplete}%` }}
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {/* Total Rows */}
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-1">{t('bulk_import_total_rows')}</p>
          <p className="text-2xl font-bold text-gray-900">{status.totalRows}</p>
        </div>

        {/* Processed */}
        <div className="bg-soft-mint rounded-lg p-4">
          <p className="text-xs text-dark-teal mb-1">{t('bulk_import_processed')}</p>
          <p className="text-2xl font-bold text-dark-teal">{status.processedRows}</p>
        </div>

        {/* Success */}
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-green-600 mb-1">{t('bulk_import_success')}</p>
          <p className="text-2xl font-bold text-green-900">{status.successCount}</p>
        </div>

        {/* Failures */}
        <div className="bg-red-50 rounded-lg p-4">
          <p className="text-xs text-red-600 mb-1">{t('bulk_import_failures')}</p>
          <p className="text-2xl font-bold text-red-900">{status.failureCount}</p>
        </div>
      </div>

      {/* Processing Message */}
      {(status.status === 'PENDING' || status.status === 'PROCESSING') && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <div className="animate-spin">
            <Icon name={ICONS.CLOCK} size="sm" ariaHidden />
          </div>
          <span>
            {t('bulk_import_processing_message', {
              processed: status.processedRows,
              total: status.totalRows
            })}
          </span>
        </div>
      )}

      {/* File Info */}
      <div className="text-sm text-gray-500 space-y-1">
        <p>
          <span className="font-medium">{t('bulk_import_file_label')}:</span> {status.fileName}
        </p>
        <p>
          <span className="font-medium">{t('bulk_import_started_label')}:</span>{' '}
          {formatDateTime(status.createdAt)}
        </p>
        {status.completedAt && (
          <p>
            <span className="font-medium">{t('bulk_import_completed_label')}:</span>{' '}
            {formatDateTime(status.completedAt)}
          </p>
        )}
      </div>
    </div>
  );
};
